import { useState } from "react";
import { X, Sparkles, Loader2, User, FolderKanban, History, ListChecks, MessageSquare, AlertCircle, Lightbulb, Wand2, ArrowLeft } from "lucide-react";
import { axiosInstance } from "../../lib/axios";
import toast from "react-hot-toast";

interface Briefing {
  clientBackground: string; projectStatus: string; previousInteractions: string;
  agenda: string[]; discussionPoints: string[]; outstandingActions: string[]; recommendations: string[];
}

// AI Meeting Briefing Coach — prepares the BD before a scheduled client meeting.
// The BD first drops in any project details they know, then AI builds the briefing
// around them (with whatever the system already has on file).
export const MeetingBriefingModal = ({ meetingId, onClose }: { meetingId: string; onClose: () => void }) => {
  const [phase, setPhase] = useState<"input" | "loading" | "result">("input");
  const [details, setDetails] = useState("");
  const [briefing, setBriefing] = useState<Briefing | null>(null);
  const [meta, setMeta] = useState<{ title?: string; type?: string; client?: string; when?: string } | null>(null);
  const [ai, setAi] = useState(false);

  const generate = async () => {
    setPhase("loading");
    try {
      const { data } = await axiosInstance.post(`/activities/${meetingId}/briefing`, { details: details.trim() });
      setBriefing(data.briefing); setMeta(data.meeting); setAi(!!data.aiGenerated);
      setPhase("result");
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Couldn't generate briefing");
      setPhase("input");
    }
  };

  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl max-h-[92vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-br from-[#0058c3] to-[#0070f3] px-6 py-5 text-white flex items-start justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-white/15 grid place-items-center"><Sparkles size={18} /></div>
            <div>
              <h2 className="text-[17px] font-bold leading-tight">AI Meeting Briefing</h2>
              <p className="text-[12px] text-blue-100">{meta?.title || "Meeting prep"}{meta?.client ? ` · ${meta.client}` : ""}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-white/15"><X size={18} /></button>
        </div>

        {/* Step 1 — ask the BD for project details */}
        {phase === "input" && (
          <div className="p-6 space-y-4">
            <div>
              <h3 className="text-[15px] font-bold text-on-surface flex items-center gap-1.5"><Wand2 size={16} className="text-primary" /> Add project details</h3>
              <p className="text-[13px] text-on-surface-variant mt-1 leading-relaxed">
                Tell us what you know about this project so we can prepare a sharper briefing for you — objectives,
                features, timeline, budget, or anything the client mentioned. The more you add, the better your prep.
              </p>
            </div>
            <textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              rows={7}
              autoFocus
              placeholder={"e.g. Client wants an e-commerce mobile app with online payments and order tracking. Budget around $8k, wants a demo in 6 weeks. Concerned about delivery timeline. Prefers React Native..."}
              className="w-full border border-outline-variant rounded-xl px-3.5 py-3 text-[13px] focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
            />
            <p className="text-[11px] text-on-surface-variant">Optional — you can generate a briefing without this, but adding details makes it far more useful.</p>
            <div className="flex justify-between gap-3 pt-1">
              <button onClick={generate} className="px-4 py-2.5 rounded-lg border border-outline-variant text-[13px] font-semibold text-on-surface-variant hover:bg-surface-container-low">
                Skip — just use what's on file
              </button>
              <button onClick={generate} className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg bg-primary text-white text-[13px] font-semibold hover:bg-[#004b9e]">
                <Sparkles size={15} /> Generate Briefing
              </button>
            </div>
          </div>
        )}

        {phase === "loading" && (
          <div className="py-16 grid place-items-center gap-3 text-on-surface-variant">
            <Loader2 size={30} className="animate-spin text-primary" />
            <p className="text-sm">Preparing your briefing…</p>
          </div>
        )}

        {phase === "result" && briefing && (
          <>
            <div className="p-6 overflow-y-auto space-y-5">
              {!ai && <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">AI unavailable — showing a basic briefing from available data.</p>}
              <Para icon={User} title="Client Background" text={briefing.clientBackground} />
              <Para icon={FolderKanban} title="Project Status" text={briefing.projectStatus} />
              <Para icon={History} title="Previous Interactions" text={briefing.previousInteractions} />
              <Bullets icon={ListChecks} title="Meeting Agenda" items={briefing.agenda} />
              <Bullets icon={MessageSquare} title="Recommended Discussion Points" items={briefing.discussionPoints} />
              <Bullets icon={AlertCircle} title="Outstanding Action Items" items={briefing.outstandingActions} />
              <Bullets icon={Lightbulb} title="Preparation Tips" items={briefing.recommendations} highlight />
            </div>
            <div className="p-4 border-t border-outline-variant bg-surface-container-low/50 flex justify-between gap-3">
              <button onClick={() => setPhase("input")} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-outline-variant text-[13px] font-semibold text-on-surface-variant hover:bg-surface-container-low">
                <ArrowLeft size={15} /> Add more details
              </button>
              <button onClick={onClose} className="px-5 py-2 rounded-lg bg-primary text-white text-[13px] font-semibold hover:bg-[#004b9e]">Got it</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const Para = ({ icon: Icon, title, text }: { icon: any; title: string; text: string }) => (
  <div>
    <h4 className="text-[12px] font-bold text-on-surface flex items-center gap-1.5 mb-1"><Icon size={14} className="text-primary" /> {title}</h4>
    <p className="text-[13px] text-on-surface-variant leading-relaxed">{text}</p>
  </div>
);
const Bullets = ({ icon: Icon, title, items, highlight }: { icon: any; title: string; items: string[]; highlight?: boolean }) => (
  <div>
    <h4 className="text-[12px] font-bold text-on-surface flex items-center gap-1.5 mb-1.5"><Icon size={14} className={highlight ? "text-amber-500" : "text-primary"} /> {title}</h4>
    <ul className="space-y-1">
      {(items || []).map((it, i) => (
        <li key={i} className="text-[13px] text-on-surface-variant flex items-start gap-2">
          <span className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${highlight ? "bg-amber-400" : "bg-primary"}`} /> {it}
        </li>
      ))}
    </ul>
  </div>
);
